/**
 * LineEmotionPicker.jsx
 *
 * HOW a line is delivered — overruled where the line is, which is where you find out it is wrong.
 *
 * ⭐ WHY THIS EXISTS. A line's `emotion` is the ONLY input to the audio tag the model receives: it
 * resolves through the category registry to one of nine presets, and the preset supplies the tag and
 * the voice settings. The classifier assigns it at import from a fixed list, at temperature 0, from the
 * surrounding narration — a reasonable guess and no more. The operator's example, which is the whole
 * case for this control: Diogenes saying *"Of course I was a spy"* came out labelled `neutral`, and
 * `neutral` is the one preset that sends no tag at all. *"That wouldn't be a neutral typically."*
 *
 * ⛔ THE VOCABULARY COMES FROM THE SERVER, and it is not the importer's list. It is the labels that
 * have a REGISTRY ROW, because an unmapped label is not an error anywhere — it falls to `neutral` and
 * renders with no direction, looking for all the world like it worked. 21 labels from the importer's
 * own 56 were in that state until 2026-09-29, covering 26% of labelled dialogue. A picker offering
 * unmapped labels would let that be recreated by hand. The operator's words: *"fill the selector with
 * 'knowns' — or we'd make a mess."*
 *
 * ⭐ AND THE CATEGORY IS SHOWN BESIDE EVERY LABEL, because the category is what actually picks the tag.
 * Two labels an operator believes are different may resolve identically — `wry` and `sardonic` are both
 * `sarcastic` — and the moment of choosing is the only honest place to say so.
 *
 * ⚠️ CHANGING THIS CHANGES NO AUDIO. The line keeps the take it has, made under the old label. The
 * amber mark says so; re-rendering the line is what applies it, and costs characters, not operations.
 */

import React from "react";
import { Dropdown, Icon, Popup } from "semantic-ui-react";

/**
 * One flat list, sorted by the LABEL.
 *
 * ⭐ Because that is what you arrive knowing. The first version grouped by category and sorted within
 * each group, which reads well as an explanation of the nine presets and badly as a thing to find
 * "incredulous" in — you would have to know it lives under `surprise` before you could look it up. The
 * operator, on using it: *"I personally find it more intuitive if it was sorted by the replacement text."*
 *
 * ⚠️ The category still travels with every row, because it is the consequence: it decides the audio tag
 * and the voice settings, and two labels that feel distinct may resolve to the same preset. Sorting
 * moved it from the menu's structure into each line of it, where it is read rather than navigated.
 */
/**
 * ⛔ MEMOISED, AND THAT IS NOT A MICRO-OPTIMISATION. This was called inline in every row's render, so a
 * whole-book line set — 1,199 rows in Mask — sorted and mapped 123 labels once PER ROW, rebuilding roughly
 * 147,000 option objects on every re-render. The symptom was the browser's own "Page Unresponsive — Wait"
 * dialog, which is not a message this app produces and so looked like something else entirely.
 *
 * ⚠️ Keyed on the vocabulary OBJECT, which is fetched once per mount and never mutated, so a WeakMap both
 * hits every time and lets the entry go when the vocabulary is replaced.
 */
const OPTIONS_CACHE = new WeakMap();
/**
 * ⭐ EXPORTED so LineJoinModal builds its emotion list from the SAME source, cache included. A join
 * chooses the delivery for a line whose fragments were never classified, and a second hand-rolled
 * mapping would drift from this one — including the audioTag description, which is the only place the
 * operator can see what will actually be sent.
 */
export const optionsFor = (vocabulary) => {
  if (!vocabulary) return [];
  const hit = OPTIONS_CACHE.get(vocabulary);
  if (hit) return hit;
  const built = buildOptions(vocabulary);
  OPTIONS_CACHE.set(vocabulary, built);
  return built;
};

const buildOptions = (vocabulary) => (
  [...((vocabulary && vocabulary.labels) || [])]
    .sort((a, b) => String(a.emotion).localeCompare(String(b.emotion)))
    .map((l) => ({
      key: l.emotion,
      value: l.emotion,
      text: l.emotion,
      /**
       * ⭐ THE TAG, WHERE A LABEL HAS ITS OWN — otherwise the category, which is what decides the tag for it.
       * A bucket name says nothing about delivery; the tag is the actual instruction. Showing it means
       * SELECTING a label now shows you exactly what will be sent, which is the loop closed.
       */
      description: l.audioTag ? l.audioTag : l.category,
    }))
);

/**
 * The inline control on a line-set row. Reads as the emotion chip it replaces until you click it, so
 * 150 rows of ordinary dialogue stay exactly as legible as before.
 */
export const LineEmotionCell = ({ row, vocabulary, onSetEmotion, busy }) => {
  // See the note above the return: a row is a cheap chip until someone clicks it.
  const [engaged, setEngaged] = React.useState(false);
  const current = row.emotion || null;
  // No vocabulary yet (or no handler) → the old read-only chip. ⛔ Better to show the label than to
  // offer a menu of guesses, which is the same bargain LineKindPicker makes.
  if (!vocabulary || !(vocabulary.labels || []).length || !onSetEmotion) {
    return <span className="rud-emotion-chip">{current || "—"}</span>;
  }

  const entry = (vocabulary.labels || []).find((l) => l.emotion === current) || null;
  const category = entry ? entry.category : null;
  // ⭐ What the menu and the tooltip name: the label's own tag when it has one, else its category.
  const tagOrCategory = (entry && entry.audioTag) ? entry.audioTag : category;
  /**
   * ⛔ THE LABEL IS SET AND THE AUDIO PREDATES IT. `renderedEmotion` is the emotion the existing take
   * was rendered under, so this is the one state that would otherwise be invisible: the chip says
   * `incredulous`, the row plays, and what you hear is the `neutral` take from before the change.
   * ⚠️ Only when there IS a take — an unrendered line is not stale, it is unrendered.
   */
  const stale = !!(row.renderedEmotion && current && row.renderedEmotion !== current);
  /**
   * ⚠️ A label the registry does not know, which can only arrive from the classifier inventing one
   * (32 of those exist across the corpus). It renders as untagged neutral, and the row is the only
   * place that fact is visible.
   */
  const unmapped = !!(current && !entry);

  const cls = stale
    ? "rud-emotion-chip rud-emotion-chip-stale"
    : unmapped
      ? "rud-emotion-chip rud-emotion-chip-unmapped"
      : "rud-emotion-chip";

  /**
   * ⭐ WHAT ACTUALLY REACHED ELEVENLABS, which nothing showed until the operator asked for it.
   *
   * The row displays the book's words; the audio tag comes from the preset's `renderTemplate` (plus the
   * narration override), so the string the model received was invisible — and the tag is now the main lever
   * on how a line sounds. `renderedAudioTag` is stamped at render time, so this is a record rather than a
   * recomputation: it says what WAS sent, not what would be sent now. Those differ the moment an emotion is
   * overruled or a preset is retuned, and the difference is the whole reason to show it.
   *
   * ⚠️ "no tag" is a real answer, not a gap — every `neutral` dialogue line is sent bare.
   */
  const sentLine = row.uuid
    ? (row.renderedAudioTag
      ? `Sent to ElevenLabs as: ${row.renderedAudioTag} + the line's text.`
      : "Sent with NO audio tag — the text alone.")
    : "Not rendered yet, so nothing has been sent.";

  const content = stale
    ? `Set to “${current}”${category ? ` (${category})` : ""}, but the audio you can play here was `
      + `rendered as “${row.renderedEmotion}”. Re-render this line to hear the change. — ${sentLine}`
    : unmapped
      ? `“${current}” has no category mapping, so this line renders with NO audio tag — the same as `
        + `neutral. Pick a known label, or add a mapping in Admin. — ${sentLine}`
      : `${current} → ${tagOrCategory} — ${entry && entry.audioTag ? "this label's own audio tag; voice settings still come from " + category : "the preset that supplies this line's audio tag and voice settings"}. `
        + `— ${sentLine}`;

  /**
   * ⭐⭐ THE CHIP UNTIL YOU CLICK IT, and this is what keeps a whole-book line set usable.
   *
   * A searchable Semantic `Dropdown` carrying 123 options is an expensive component, and the table mounts one
   * PER ROW. At 1,199 rows — Mask's whole book — that alone froze the browser hard enough to raise its own
   * "Page Unresponsive — Wait" dialog. The operator hit it, and the message looked unrelated because it is
   * not text this app produces.
   *
   * So the cell renders as the chip it used to be, and becomes a dropdown only once it is engaged: 1,199
   * cheap spans instead of 1,199 dropdowns, and the one you clicked opens immediately via `defaultOpen`.
   *
   * ⚠️ `engaged` deliberately never resets. A cell you have used stays a live control for the rest of the
   * session — resetting it would make a second change to the same line need two clicks, and one row's
   * dropdown costs nothing.
   */
  const chip = (
    <Popup
      size="mini"
      content={content}
      trigger={
        <span className="rud-cell-trigger">
          <span className={cls}>
            {current || "—"}
            {stale && <Icon name="clock outline" color="orange" size="small" style={{ marginLeft: ".3em" }} />}
            {unmapped && <Icon name="warning sign" color="orange" size="small" style={{ marginLeft: ".3em" }} />}
          </span>
        </span>
      }
    />
  );

  if (!engaged) {
    return (
      <span
        role="button"
        tabIndex={0}
        className="rud-emotion-engage"
        onClick={() => setEngaged(true)}
        // Keyboard-reachable for the same reason the Line ID's popup trigger is.
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEngaged(true); } }}
      >
        {chip}
      </span>
    );
  }

  return (
    <Dropdown
      className="rud-emotion-picker"
      inline
      defaultOpen
      disabled={!!busy}
      loading={!!busy}
      options={optionsFor(vocabulary)}
      value={current || null}
      // ⚠️ No "clear" option, unlike the kind picker: every line HAS a delivery, and an empty emotion
      // resolves to neutral anyway. Offering "— none —" would dress that up as a third state.
      onChange={(_, d) => { if (d.value && d.value !== current) onSetEmotion([row.lineId], d.value); }}
      search
      selectOnBlur={false}
      trigger={chip}
    />
  );
};

/**
 * The same choice for every ticked line at once.
 *
 * ⭐ WHY BULK MATTERS HERE MORE THAN FOR KINDS. The operator's reason for building this: *"We've got
 * many books to build… I suspect many 'neutral's could be tuned even after a single listen."* A
 * character's tic — a habitual dryness, a nervous hedge — is a pattern across dozens of lines, and
 * fixing it one row at a time is how a pass gets abandoned half-done.
 */
export const LineEmotionBulkPicker = ({ vocabulary, count, onSetEmotion, busy }) => {
  if (!vocabulary || !(vocabulary.labels || []).length || !onSetEmotion || !count) return null;
  return (
    <Dropdown
      button
      basic
      compact
      className="icon rud-emotion-bulk"
      icon="theme"
      floating
      labeled
      search
      disabled={!!busy}
      loading={!!busy}
      text={`Set emotion (${count})`}
      options={optionsFor(vocabulary)}
      value={null}
      selectOnBlur={false}
      onChange={(_, d) => { if (d.value) onSetEmotion(null, d.value); }}
    />
  );
};

export default LineEmotionCell;
